import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Avatar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    LinearProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useTranslation } from 'react-i18next';
import FileDropBoxArea from 'components/shared/file-drop-box-area/file-drop-box-area.component';
import Loader from 'components/shared/loader/loader.component';
import {
    fetchImportBatch,
    readRequestError,
    startHistoricalImport,
    uploadAttachmentChunk,
} from './historical-import.api';
import {
    ATTACHMENT_CHUNK_SIZE,
    BATCH_POLL_INTERVAL_MS,
    HISTORICAL_SOURCE_FILE_KINDS,
    HistoricalImportBatch,
    HistoricalImportStatus,
    HistoricalSourceFileKind,
    HistoricalSourceFiles,
    REQUIRED_SOURCE_FILE_KIND,
    SOURCE_FILE_KIND_LABEL_KEYS,
    StagedFile,
} from './historical-import.types';

/**
 * DD-3687: imports a customer's order history into the read-only archive.
 *
 * Deliberately not folded into the order import beside it. That one sends
 * everything in a single request and waits; a historical archive is 100,000+
 * orders, so this runs in three phases against one batch id:
 *
 *   1. the export's five files are handed over and a batch id comes back,
 *   2. the dialog polls while the server validates and writes,
 *   3. the attachments go up in chunks, each safe to re-send.
 *
 * The operator is not asked to wait on one long request at any point, and
 * closing the dialog mid-run does not lose the run — the server holds the
 * progress.
 */

type ImportHistoricalOrdersDialogProps = {
    open: boolean;
    onCloseDialog: () => void;
    labId?: string;
};

const SOURCE_ACCEPT = {
    'text/csv': ['.csv'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

const LEGACY_XLS_PATTERN = /\.xls$/i;

const isRunning = (status?: HistoricalImportStatus) =>
    status === HistoricalImportStatus.VALIDATING || status === HistoricalImportStatus.WRITING;

const ImportHistoricalOrdersDialog: FC<ImportHistoricalOrdersDialogProps> = ({
    open,
    onCloseDialog,
    labId,
}: ImportHistoricalOrdersDialogProps) => {
    const { t: translate } = useTranslation(['users', 'orders', 'common']);

    const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [starting, setStarting] = useState(false);
    const [importBatchId, setImportBatchId] = useState('');
    const [batch, setBatch] = useState<HistoricalImportBatch | null>(null);
    const [attachmentsSent, setAttachmentsSent] = useState(0);
    const [uploadingAttachments, setUploadingAttachments] = useState(false);
    const [rejectedAttachments, setRejectedAttachments] = useState<string[]>([]);
    const [closeWarningShown, setCloseWarningShown] = useState(false);

    // Kept in a ref so the poll can be stopped from the unmount cleanup.
    const pollTimer = useRef<ReturnType<typeof setInterval>>();

    useEffect(() => {
        if (!labId || !importBatchId || !isRunning(batch?.status ?? HistoricalImportStatus.VALIDATING)) {
            return undefined;
        }
        pollTimer.current = setInterval(async () => {
            try {
                setBatch(await fetchImportBatch(labId, importBatchId));
            } catch (error) {
                setErrorMessage(readRequestError(error) || translate('common:AN_ERROR_OCCURRED'));
            }
        }, BATCH_POLL_INTERVAL_MS);
        return () => clearInterval(pollTimer.current);
    }, [labId, importBatchId, batch?.status, translate]);

    const resetDialog = () => {
        clearInterval(pollTimer.current);
        setStagedFiles([]);
        setAttachments([]);
        setErrorMessage('');
        setImportBatchId('');
        setBatch(null);
        setAttachmentsSent(0);
        setRejectedAttachments([]);
    };

    /**
     * A run keeps going on the server after the dialog is closed, so closing is
     * not a cancel. Worse, closing while attachments are outstanding strands
     * them: the orders are already searchable and the files can never be added.
     * So the first Close asks, and the second one goes through.
     *
     * The batch is deliberately *not* reset when a run is still in flight —
     * the component stays mounted (see import-file-dialog), so reopening comes
     * back to the same batch rather than a blank form.
     */
    const runInFlight =
        !!importBatchId && (isRunning(batch?.status) || batch?.status === HistoricalImportStatus.AWAITING_ATTACHMENTS);

    const handleClose = () => {
        if (runInFlight && !closeWarningShown) {
            setCloseWarningShown(true);
            return;
        }
        if (!runInFlight) {
            resetDialog();
        }
        setCloseWarningShown(false);
        onCloseDialog();
    };

    /** Every CSV is dropped on the same area; its type is chosen in the list below. */
    const handleStageSourceFiles = (droppedFiles: File[]) => {
        setErrorMessage('');
        // FileDropBoxArea's prop says File[], but only the drop path delivers one:
        // the click-to-browse path hands over `event.target.files`, a FileList
        // cast to File[]. A FileList has length and indexing but no array
        // methods, so normalise before touching it.
        const normalised = Array.from(droppedFiles ?? []);
        if (!normalised.length) {
            return;
        }
        if (normalised.some((file) => LEGACY_XLS_PATTERN.test(file.name))) {
            // Caught here so the operator finds out before uploading, not after.
            setErrorMessage(translate('orders:HISTORICAL_IMPORT_LEGACY_XLS'));
            return;
        }
        setStagedFiles((current) => [...current, ...normalised.map((file) => ({ file }))]);
    };

    const handleSelectKind = (event: SelectChangeEvent, staged: StagedFile) => {
        const kind = event.target.value as HistoricalSourceFileKind;
        setStagedFiles((current) => current.map((entry) => (entry === staged ? { ...entry, kind } : entry)));
    };

    /** A type already chosen cannot be chosen again — one file of each kind. */
    const isKindTaken = (kind: HistoricalSourceFileKind) => stagedFiles.some((staged) => staged.kind === kind);

    const sourceFiles: HistoricalSourceFiles = Object.fromEntries(
        stagedFiles.filter((staged) => staged.kind).map((staged) => [staged.kind, staged.file]),
    );

    // Every file needs a type, and the orders file has to be among them: the
    // other four describe orders, so without it there is nothing to join to.
    const canStart =
        stagedFiles.length > 0 &&
        stagedFiles.every((staged) => !!staged.kind) &&
        !!sourceFiles[REQUIRED_SOURCE_FILE_KIND] &&
        !starting;

    const handleStart = async () => {
        if (!labId) {
            return;
        }
        try {
            setStarting(true);
            setErrorMessage('');
            const started = await startHistoricalImport(labId, sourceFiles);
            setImportBatchId(started.importBatchId);
            setBatch({ _id: started.importBatchId, status: started.status });
        } catch (error) {
            setErrorMessage(readRequestError(error) || translate('common:AN_ERROR_OCCURRED'));
        } finally {
            setStarting(false);
        }
    };

    /**
     * Sends the attachments a chunk at a time, stopping on the first chunk the
     * server refuses. Re-running after a failure is safe — files already
     * stored come back as "skipped".
     */
    const handleUploadAttachments = async () => {
        if (!labId || !importBatchId) {
            return;
        }
        setUploadingAttachments(true);
        setErrorMessage('');
        setRejectedAttachments([]);
        try {
            for (let index = 0; index < attachments.length; index += ATTACHMENT_CHUNK_SIZE) {
                const chunk = attachments.slice(index, index + ATTACHMENT_CHUNK_SIZE);
                const result = await uploadAttachmentChunk(labId, importBatchId, chunk);
                setAttachmentsSent(index + chunk.length);
                setRejectedAttachments((rejected) => [
                    ...rejected,
                    ...result.results
                        .filter((outcome) => outcome.outcome === 'rejected' || outcome.outcome === 'failed')
                        .map((outcome) => `${outcome.fileName}${outcome.reason ? ` — ${outcome.reason}` : ''}`),
                ]);
                setBatch((current) =>
                    current
                        ? {
                              ...current,
                              status: result.status,
                              attachmentsStored: result.attachmentsStored,
                              attachmentsExpected: result.attachmentsExpected,
                          }
                        : current,
                );
            }
        } catch (error) {
            setErrorMessage(readRequestError(error) || translate('common:AN_ERROR_OCCURRED'));
        } finally {
            setUploadingAttachments(false);
        }
    };

    const hasStarted = !!importBatchId;
    const awaitingAttachments = batch?.status === HistoricalImportStatus.AWAITING_ATTACHMENTS;
    const failed = batch?.status === HistoricalImportStatus.FAILED;
    const complete = batch?.status === HistoricalImportStatus.COMPLETE;

    return (
        <Dialog fullWidth maxWidth="lg" open={open} onClose={handleClose}>
            <DialogTitle>{translate('orders:HISTORICAL_IMPORT_TITLE')}</DialogTitle>
            <DialogContent>
                {errorMessage && (
                    <Box mb={2}>
                        <Alert severity="error">{errorMessage}</Alert>
                    </Box>
                )}

                {!hasStarted && (
                    <Fragment>
                        <Alert severity="info" sx={{ marginBottom: 2 }}>
                            {translate('orders:HISTORICAL_IMPORT_INSTRUCTIONS')}
                        </Alert>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FileDropBoxArea accept={SOURCE_ACCEPT} handleChange={handleStageSourceFiles} />
                            </Grid>
                            {stagedFiles.length ? (
                                <Grid item xs={12}>
                                    <Typography variant="h3">{translate('common:FILES')}</Typography>
                                    <List dense>
                                        {stagedFiles.map((staged) => (
                                            <ListItem key={staged.file.name}>
                                                <ListItemAvatar>
                                                    <Avatar>
                                                        <InsertDriveFileIcon />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText primary={staged.file.name} />
                                                <FormControl sx={{ width: '25%' }}>
                                                    <InputLabel>{translate('users:TYPE_OF_IMPORT')}</InputLabel>
                                                    <Select
                                                        value={staged.kind ?? ''}
                                                        label={translate('users:TYPE_OF_IMPORT')}
                                                        onChange={(event) => handleSelectKind(event, staged)}
                                                        data-test={`historical-import.type.${staged.file.name}`}
                                                    >
                                                        {HISTORICAL_SOURCE_FILE_KINDS.map((kind) => (
                                                            <MenuItem
                                                                key={kind}
                                                                value={kind}
                                                                disabled={isKindTaken(kind) && staged.kind !== kind}
                                                            >
                                                                {translate(SOURCE_FILE_KIND_LABEL_KEYS[kind])}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Grid>
                            ) : undefined}
                        </Grid>
                    </Fragment>
                )}

                {starting && <Loader />}

                {hasStarted && isRunning(batch?.status) && (
                    <Box mb={2}>
                        <Typography variant="body2" gutterBottom>
                            {batch?.status === HistoricalImportStatus.VALIDATING
                                ? translate('orders:HISTORICAL_IMPORT_VALIDATING', {
                                      count: batch?.ordersValidated ?? 0,
                                  })
                                : translate('orders:HISTORICAL_IMPORT_WRITING', {
                                      count: batch?.ordersWritten ?? 0,
                                  })}
                        </Typography>
                        <LinearProgress />
                    </Box>
                )}

                {failed && (
                    <Alert severity="error">
                        <AlertTitle>
                            {translate('orders:HISTORICAL_IMPORT_FAILED', {
                                count: batch?.totalFailures ?? 0,
                            })}
                        </AlertTitle>
                        {translate('orders:HISTORICAL_IMPORT_NOTHING_WRITTEN')}
                        <List dense>
                            {(batch?.failures ?? []).map((failure) => (
                                <ListItem key={`${failure.sourceFile}-${failure.row}-${failure.reason}`}>
                                    <ListItemText
                                        primary={`${failure.sourceFile} ${translate('orders:HISTORICAL_IMPORT_ROW')} ${failure.row}`}
                                        secondary={failure.reason}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Alert>
                )}

                {hasStarted && !failed && (
                    <Box mt={2}>
                        <Alert severity={complete ? 'success' : 'info'}>
                            <AlertTitle>
                                {translate('orders:HISTORICAL_IMPORT_BATCH_ID')}: {importBatchId}
                            </AlertTitle>
                            {translate('orders:HISTORICAL_IMPORT_ORDERS_WRITTEN', {
                                count: batch?.ordersWritten ?? 0,
                            })}
                        </Alert>
                    </Box>
                )}

                {awaitingAttachments && (
                    <Box mt={2}>
                        <Typography variant="h3" gutterBottom>
                            {translate('orders:HISTORICAL_IMPORT_ATTACHMENTS')}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                            {translate('orders:HISTORICAL_IMPORT_ATTACHMENTS_OUTSTANDING', {
                                stored: batch?.attachmentsStored ?? 0,
                                expected: batch?.attachmentsExpected ?? 0,
                            })}
                        </Typography>
                        <FileDropBoxArea
                            handleChange={(files) =>
                                // Array.from for the same reason as above: the
                                // browse path delivers a FileList, not an array.
                                setAttachments([...attachments, ...Array.from(files ?? [])])
                            }
                        />
                        {attachments.length > 0 && (
                            <Typography variant="body2">
                                {translate('orders:HISTORICAL_IMPORT_ATTACHMENTS_STAGED', {
                                    count: attachments.length,
                                })}
                            </Typography>
                        )}
                        {uploadingAttachments && (
                            <Box mt={1}>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.round((attachmentsSent / attachments.length) * 100)}
                                />
                                <Typography variant="caption">
                                    {attachmentsSent} / {attachments.length}
                                </Typography>
                            </Box>
                        )}
                        {rejectedAttachments.length > 0 && (
                            <Alert severity="warning" sx={{ marginTop: 1 }}>
                                <AlertTitle>{translate('orders:HISTORICAL_IMPORT_FILES_REFUSED')}</AlertTitle>
                                {rejectedAttachments.join(', ')}
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {closeWarningShown && (
                    <Alert severity="warning" sx={{ flexGrow: 1, marginRight: 1 }}>
                        {awaitingAttachments
                            ? translate('orders:HISTORICAL_CLOSE_WARNING_ATTACHMENTS')
                            : translate('orders:HISTORICAL_CLOSE_WARNING_RUNNING')}
                    </Alert>
                )}
                <Button
                    onClick={handleClose}
                    disabled={starting || uploadingAttachments}
                    color={closeWarningShown ? 'warning' : 'primary'}
                    data-test="historical-import.close"
                >
                    {closeWarningShown ? translate('orders:HISTORICAL_CLOSE_ANYWAY') : translate('common:CLOSE')}
                </Button>
                {!hasStarted && (
                    <Button
                        variant="contained"
                        disabled={!canStart}
                        onClick={handleStart}
                        data-test="historical-import.start"
                    >
                        {translate('users:IMPORT')}
                    </Button>
                )}
                {awaitingAttachments && (
                    <Button
                        variant="contained"
                        disabled={attachments.length === 0 || uploadingAttachments}
                        onClick={handleUploadAttachments}
                        data-test="historical-import.upload-attachments"
                    >
                        {translate('orders:HISTORICAL_IMPORT_UPLOAD_ATTACHMENTS')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ImportHistoricalOrdersDialog;
